import { createMemo, createSignal } from 'solid-js';
import { annualDistribution, isPriced, price as instrumentPrice } from '~data';
import { todayISO } from '~/lib/format';
import { deductibleShare, equity, loans, resolved, totalCash, totalDebt, totalIncome, totalInterest, totalValue } from '~/stores/portfolio-store';
import { profile } from '~/stores/profile-store';

// Global assumptions. Per-instrument distribution growth lives on the
// instrument (data/instruments.ts); these apply across the whole book.
//
// Tax is deliberately not modelled. It depends on the character of each
// instrument's distribution (eligible dividend, return of capital, foreign
// income, capital gain), on whether the account is registered, and on total
// income — a single flat rate would be wrong in every one of those dimensions.
// Everything below is therefore pre-tax and labelled as such.
export const [years, setYears] = createSignal(10);
export const [priceGrowth, setPriceGrowth] = createSignal(4);
export const [drip, setDrip] = createSignal(true);
/** Route net income to the loans instead of to units. */
export const [payDownDebt, setPayDownDebt] = createSignal(false);

export interface YearRow {
    year: number;
    value: number;
    distributions: number;
    interest: number;
    /** Distributions less interest. Before any tax. */
    netIncome: number;
    debt: number;
    equity: number;
    cumulativeNet: number;
}

interface SimPosition {
    units: number;
    price: number;
    /** Annual distribution per unit. */
    distribution: number;
    growth: number;
    sheltered: boolean;
}

// Null when anything held is unpriced. A forecast built on some positions and
// silently missing others is worse than no forecast, so the pane says so
// instead.
const snapshot = (): SimPosition[] | null => {
    const asOf = todayISO();
    const rows = resolved();
    if (rows.length === 0 || rows.some((r) => !isPriced(r.symbol, asOf))) return null;
    return rows.map((r) => ({
        units: r.holding.units,
        price: instrumentPrice(r.symbol)!,
        distribution: annualDistribution(r.symbol, asOf)!,
        // No published growth assumption; distributions are held flat rather
        // than trended on a number nobody supplied.
        growth: 0,
        sheltered: r.sheltered,
    }));
};

// Year-by-year projection over the whole book. Each position compounds its own
// distribution at its own rate; prices all move at the global assumption.
// Carrying charges on deductible loans offset taxable distribution income,
// which is the normal treatment and the reason the account split matters.
export const projection = createMemo<YearRow[]>(() => {
    const rows: YearRow[] = [];
    const positions = snapshot();
    if (positions == null) return rows;
    // Deductibility is carried as the share of each loan that went into a
    // taxable account. Paying a loan down keeps that share fixed, which assumes
    // repayments come off each allocation evenly rather than off the deductible
    // portion first.
    const balances = loans().map((l) => ({ balance: l.balance, rate: l.rate, share: deductibleShare(l) }));
    const horizon = Math.max(0, Math.round(years()));

    // Uninvested cash is part of the portfolio's value and has to be carried,
    // or the forecast opens below what the accounts pane already shows. Held
    // flat: it earns no distribution and is not assumed to be deployed.
    const cash = totalCash();

    let cumulativeNet = 0;

    for (let y = 1; y <= horizon; y++) {
        if (y > 1) {
            for (const p of positions) {
                p.distribution *= 1 + p.growth / 100;
                p.price *= 1 + priceGrowth() / 100;
            }
        }

        const interest = balances.reduce((sum, b) => sum + (b.balance * b.rate) / 100, 0);
        const distributions = positions.reduce((sum, p) => sum + p.units * p.distribution, 0);
        const netIncome = distributions - interest;

        if (payDownDebt()) {
            let left = Math.max(0, netIncome);
            for (const b of balances) {
                const paid = Math.min(b.balance, left);
                b.balance -= paid;
                left -= paid;
                if (left <= 0) break;
            }
        } else if (drip()) {
            for (const p of positions) {
                if (p.price <= 0) continue;
                // Gross, since tax is not modelled — in a registered account
                // this is exact, in a taxable one it reinvests more than would
                // really be available.
                p.units += (p.units * p.distribution) / p.price;
            }
        }

        const value = cash + positions.reduce((sum, p) => sum + p.units * p.price, 0);
        const debt = balances.reduce((sum, b) => sum + b.balance, 0);
        cumulativeNet += netIncome;

        rows.push({ year: y, value, distributions, interest, netIncome, debt, equity: value - debt, cumulativeNet });
    }

    return rows;
});

/**
 * The first projected year the book pays the goal, or null if it does not get
 * there inside the horizon.
 *
 * Gross distributions, matching the goal itself and the "Per month" figure
 * everywhere else. Deliberately not net of interest: the goal is what the
 * holdings pay, and netting the carry off it would make paying a loan down look
 * like progress toward an income target it is not progress toward.
 *
 * Null does not mean never — it means not within `years()`. The two read
 * differently to anyone deciding whether the plan is close, so the caller says
 * which one it is rather than this returning a number that implies precision it
 * has not got.
 */
export const goalYear = createMemo<number | null>(() => {
    const goal = profile.goalMonthly;
    if (goal == null || goal <= 0) return null;
    return projection().find((r) => r.distributions / 12 >= goal)?.year ?? null;
});

export const summary = createMemo(() => {
    const rows = projection();
    const last = rows[rows.length - 1];
    const income = totalIncome();
    return {
        value: totalValue(),
        debt: totalDebt(),
        equity: equity(),
        income,
        interest: totalInterest(),
        /** Distributions less interest. Before any tax. */
        net: income == null ? null : income - totalInterest(),
        finalValue: last?.value ?? totalValue(),
        finalEquity: last?.equity ?? equity(),
        finalNet: last?.netIncome ?? null,
        cumulativeNet: last?.cumulativeNet ?? null,
    };
});

export const resetAssumptions = (): void => {
    setYears(10);
    setPriceGrowth(4);
    setDrip(true);
    setPayDownDebt(false);
};
